import { submitFormData } from '@/redux/actions/widgets.js';
import { isValidEmail } from '@/utils/isValidEmail';
import { TextField } from '@mui/material';
import { styled } from '@mui/material/styles';
import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { hexToRgba } from '../../../utils/common';
import { toastNotifyError } from '../../../../../Common/Toast';
import { FORM_TYPE } from '@/constants/common';

import {
  FormContainer,
  FormContent,
  FormHeadingContainer,
  FormHeading,
  FormDescription,
  FormCTAButtons,
  CTAButton,
  FormInputContainer,
  TextFieldContainer,
  ErrorField,
  FormCloseIcon,
} from './FormCFEComponent.styled.js';
import { getStyleForProps } from '@/components/DesignSystem/ModernTheme/utils/getStylesConfig';
import { useRouter } from 'next/router.js';

function CrossIcon(props) {
  const { fillColor } = props;
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width="24"
      height="24"
      viewBox="0 0 24 24"
      fill="none"
    >
      <g clip-path="url(#clip0_3356_639)">
        <path
          d="M19 6.41L17.59 5L12 10.59L6.41 5L5 6.41L10.59 12L5 17.59L6.41 19L12 13.41L17.59 19L19 17.59L13.41 12L19 6.41Z"
          fill={fillColor}
        />
      </g>
      <defs>
        <clipPath id="clip0_3356_639">
          <rect width="24" height="24" fill="white" />
        </clipPath>
      </defs>
    </svg>
  );
}

const CssTextField = styled(TextField)((props) => ({
  // label when focussed
  '& label.Mui-focused': {
    color: props.labelFocusedColor,
    opacity: 0.8,
  },
  '& .MuiInput-underline:after': {
    borderBottomColor: '#B2BAC2',
  },
  '& .MuiFormControl-root': {
    width: '100%',
  },
  '& .MuiOutlinedInput-root': {
    padding: '10px',
    width: '100%',

    '& fieldset': {
      borderColor: '#E0E3E7',
    },
    '&:hover fieldset': {
      borderColor: '#B2BAC2',
    },
    '&.Mui-focused fieldset': {
      borderColor: '#6F7E8C',
    },
  },
  '& input::placeholder': {
    color: 'blue', // Set the color for the placeholder text
  },
}));

const FormCFEComponent = (props) => {
  const { heading, description, buttonText, buttonConfig, toggleFormCompletion } = props;
  const { widgetData, storeData } = useSelector((state) => ({
    widgetData: state.widgetsReducer,
    storeData: state.storeReducer.store,
  }));
  const [propStyle, setPropStyle] = useState(storeData?.theme?.style_template);

  useEffect(() => {
    if (widgetData?.activeStyleTemplate) {
      setPropStyle(getStyleForProps(widgetData?.activeStyleTemplate));
    } else {
      setPropStyle(getStyleForProps(storeData?.theme?.style_template));
    }
  }, [widgetData?.activeStyleTemplate, storeData?.theme?.style_template]);

  const dispatch = useDispatch();
  const [formInput, setFormInput] = useState({
    name: '',
    phone: '',
    email: '',
    description: '',
  });

  useEffect(() => {
    setFormInput({
      name: '',
      phone: '',
      email: '',
      description: '',
    });
  }, [props?.open]);

  const [fieldValidity, setFieldValidity] = useState({
    name: true,
    phone: true,
    email: true,
    description: true,
  });

  const validateFields = () => {
    const isValidName = formInput.name.length >= 3;
    const isValidPhone = formInput.phone.length === 10;
    const isEmailValid = formInput.email.length === 0 || isValidEmail(formInput.email);

    setFieldValidity({
      name: isValidName,
      phone: isValidPhone,
      email: isEmailValid,
      description: true,
    });

    return isValidName && isValidPhone && isEmailValid;
  };

  const getColorsForFormInput = (type) => {
    switch (type) {
      case 'input':
        return hexToRgba(propStyle?.borderColor, 0.4);
      case 'label':
        return hexToRgba(propStyle?.borderColor, 0.8);
    }
  };

  const handleFormInputChange = (type, value) => {
    if (type === 'phone') {
      value = value.replace(/\D/g, ''); // Remove non-numeric characters
    }
    setFormInput((prev) => ({
      ...prev,
      [type]: value,
    }));
  };

  const inputStyleConfig = {
    input: {
      color: getColorsForFormInput('input'),
      '&::placeholder': {
        // <----- Add this.
        opacity: 1,
      },
    },
    label: {
      color: getColorsForFormInput('label'),
      opacity: '0.6',
      fontSize: '14px',
      fontWeight: '400',
    },
  };

  const inputStyleLastConfig = {
    input: {
      color: getColorsForFormInput('input'),
      height: '80px',
      '&::placeholder': {
        // <----- Add this.
        opacity: 1,
      },
    },
    label: {
      color: getColorsForFormInput('label'),
      opacity: '0.6',
      fontSize: '14px',
      fontWeight: '400',
    },
  };

  const handleDownload = async () => {
    if (!buttonConfig || !buttonConfig?.config?.downloadContent) return;

    // CDN path of the PDF file
    const pdfUrlPath = buttonConfig?.content[0]?.actions?.handler;
    if (pdfUrlPath === '') return;
    try {
      // Fetch the file as a blob
      const response = await fetch(pdfUrlPath);
      const blob = await response.blob();

      // Create a blob link
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;

      // Set the download attribute with the desired file name
      link.download = buttonConfig?.config?.fileName || 'Download';

      // Append the link to the document
      document.body.appendChild(link);

      // Trigger a click on the link
      link.click();

      // Remove the link from the document
      document.body.removeChild(link);

      // Release the object URL
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Error downloading file:', error);
    }
  };

  /** This function returns final payload where name and phone is must have and other keys are only sent if the value is not empty */
  const getFormData = () => {
    const finalResponse = {
      name: formInput.name,
      phone: formInput.phone,
    };

    if (formInput.email.length > 0) {
      finalResponse['email'] = formInput.email;
    }
    if (formInput.description.length > 0) {
      finalResponse['description'] = formInput.description;
    }

    return finalResponse;
  };
  const router = useRouter();
  const isBrandWebsiteDemo = router.pathname.includes('/brand-website-theme-demo');

  const submitForm = () => {
    if (!validateFields()) {
      return;
    }

    const payload = {
      store_id: props?.storeData?.store_id,
      store_theme_id: props?.storeData?.theme?.store_theme_id,
      widget_id: props.widgetId,
      theme_category: FORM_TYPE.BRAND_THEME,
      content: getFormData(),
    };

    !isBrandWebsiteDemo
      ? dispatch(
          submitFormData(payload, (status, message = '') => {
            if (status) {
              handleDownload();
              toggleFormCompletion(true);
            } else {
              toastNotifyError(message || 'Error submitting form!!');
              props.onClose();
            }
          })
        )
      : toggleFormCompletion(true);
  };

  return (
    <FormContainer themeStyle={propStyle}>
      <FormContent>
        <FormCloseIcon onClick={props.onClose}>
          <CrossIcon fillColor={propStyle?.heading?.color} />
        </FormCloseIcon>
        <FormHeadingContainer>
          <FormHeading textStyles={propStyle}>{heading}</FormHeading>
          <FormDescription textStyles={propStyle}>{description}</FormDescription>
        </FormHeadingContainer>
        <FormInputContainer>
          <TextFieldContainer>
            <CssTextField
              id="outlined-basic"
              variant="outlined"
              label="Name"
              value={formInput?.name}
              sx={inputStyleConfig}
              onChange={(e) => handleFormInputChange('name', e.target.value)}
              labelFocusedColor={getColorsForFormInput('label')}
            />
            {!fieldValidity.name && <ErrorField>Please enter a valid name</ErrorField>}
          </TextFieldContainer>
          <TextFieldContainer>
            <CssTextField
              id="outlined-basic"
              variant="outlined"
              label="Phone Number"
              sx={inputStyleConfig}
              value={formInput?.phone}
              inputProps={{
                maxLength: 10, // Set the maximum length
              }}
              onChange={(e) => handleFormInputChange('phone', e.target.value)}
              labelFocusedColor={getColorsForFormInput('label')}
            />
            {!fieldValidity.phone && (
              <ErrorField>Please enter a valid phone number</ErrorField>
            )}
          </TextFieldContainer>
          <TextFieldContainer>
            <CssTextField
              id="outlined-basic"
              variant="outlined"
              label="Email(optional)"
              sx={inputStyleConfig}
              value={formInput?.email}
              onChange={(e) => handleFormInputChange('email', e.target.value)}
              labelFocusedColor={getColorsForFormInput('label')}
            />
            {!fieldValidity.email && <ErrorField>Please enter a valid email</ErrorField>}
          </TextFieldContainer>
          <TextFieldContainer>
            <CssTextField
              id="outlined-basic"
              variant="outlined"
              label="Write your message(optional)"
              sx={inputStyleLastConfig}
              value={formInput?.description}
              onChange={(e) => handleFormInputChange('description', e.target.value)}
              labelFocusedColor={getColorsForFormInput('label')}
            />
            {!fieldValidity.description && (
              <ErrorField>Please enter a valid description</ErrorField>
            )}
          </TextFieldContainer>
        </FormInputContainer>
        <FormCTAButtons>
          <CTAButton themeStyles={propStyle} onClick={submitForm}>
            {buttonText}
          </CTAButton>
        </FormCTAButtons>
      </FormContent>
    </FormContainer>
  );
};

export default FormCFEComponent;
